/**
 * What Nexus integrates with, and on what approved terms.
 *
 * A connected system is something Nexus talks to — deliberately not the same
 * object as a canonical enterprise Application, which is an asset the enterprise
 * owns. The two share an English word and nothing else.
 *
 * The screen never shows a credential, because the API never returns one. An
 * ordinary read gives the registration without its secret references at all,
 * and the configuration — which names where a secret lives, never its value —
 * is a separate permission that most people do not hold.
 */

import { useCallback, useState } from "react";
import { api } from "../api/client";
import type {
  ConnectedSystem,
  ConnectedSystemDetail,
  IntegrationSummary,
  Page,
} from "../api/types";
import { usePagedCollection } from "../api/useCollection";
import { useResource } from "../api/useResource";
import {
  Banner,
  Card,
  Classification,
  Fact,
  Facts,
  Identifier,
  Mono,
  More,
  Pill,
  when,
  type Tone,
} from "../components/primitives";
import { Empty, Show } from "../components/states";
import { useSession } from "../session/SessionProvider";

const HEALTH_TONE: Record<string, Tone> = {
  ok: "ok",
  degraded: "wait",
  unavailable: "bad",
  disabled: "quiet",
};

const REGISTRATION_TONE: Record<string, Tone> = {
  enabled: "ok",
  registered: "quiet",
  suspended: "wait",
  retired: "quiet",
};

/**
 * The page size this screen has always asked for.
 *
 * Unchanged on purpose, for both collections. What changes is not how much is
 * read at once but whether the hundred-and-first system — or the
 * hundred-and-first registration it would have been matched against — is
 * reachable at all.
 */
const PAGE = 100;

function SystemDetail({ systemKey }: { systemKey: string }) {
  const read = useCallback(
    (signal: AbortSignal) =>
      api.get<ConnectedSystemDetail>(`/core/connected-systems/${systemKey}`, { signal }),
    [systemKey],
  );
  const { state, reload } = useResource<ConnectedSystemDetail>(read);

  return (
    <Show resource={state} what="this connected system" onRetry={reload}>
      {(detail) => (
        <Card
          title={
            <span>
              {detail.system.name}
              <span className="nx-hint" style={{ display: "block", fontWeight: 400 }}>
                {detail.system.kind.replace(/_/g, " ")}
              </span>
            </span>
          }
          actions={
            <Pill tone={HEALTH_TONE[detail.health.status] ?? "quiet"}>{detail.health.status}</Pill>
          }
        >
          <Facts>
            <Fact label="Key">
              <Mono>{detail.system.key}</Mono>
            </Fact>
            <Fact label="Linkable">{detail.system.linkable ? "Yes" : "No"}</Fact>
            <Fact label="Checked">{when(detail.health.checked_at)}</Fact>
            {detail.health.detail ? <Fact label="Detail">{detail.health.detail}</Fact> : null}
          </Facts>

          <h3 style={{ marginTop: 20, fontSize: "var(--nx-text-base)" }}>Connector contract</h3>
          {detail.contract === null ? (
            <p className="nx-hint">
              No connector contract is published for this system. It is a catalogue entry other
              records point at, not something Nexus integrates with.
            </p>
          ) : (
            <>
              <Facts>
                <Fact label="Name">{detail.contract.name}</Fact>
                <Fact label="Writes permitted">{detail.contract.writes_permitted ? "Yes" : "No"}</Fact>
                <Fact label="Obligations">{detail.contract.obligations}</Fact>
              </Facts>
              {/*
                A plain count, and it stays one. These capabilities come from
                the live connector contract the adapter layer declares — an
                in-memory object the detail read returns whole, not a page — so
                the number is the number, and `LowerBound` here would be
                manufacturing a doubt the platform does not have.
              */}
              <p style={{ fontWeight: 600, marginTop: 14 }}>
                Declared capabilities ({detail.contract.capabilities.length})
              </p>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {detail.contract.capabilities.map((capability) => (
                  <li key={capability.key}>
                    <Mono>{capability.key}</Mono> <Classification value={capability.classification} />
                    {capability.protected ? <Pill tone="wait">protected</Pill> : null}
                    <span className="nx-hint" style={{ display: "block" }}>
                      {capability.description}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}

          <h3 style={{ marginTop: 20, fontSize: "var(--nx-text-base)" }}>Connector versions</h3>
          {/*
            These are not paged here, and this screen cannot make them so.
            `GET /core/connected-systems/{key}` builds `versions` from
            `IntegrationRegistryService.versions(key)` with no page request,
            which takes the default page size, and `ConnectedSystemDetail`
            carries no `has_more` — so the response says nothing about whether
            these are all of them, and there is no truthful sentence for this
            screen to add. The paged form,
            `/core/connected-systems/{key}/versions`, is guarded by
            `core.connector_version.read`, which is a different permission from
            the `core.connected_system.read` this detail already required;
            reaching for it here would change what this card demands of a
            reader. Left alone and raised instead.
          */}
          {detail.versions.length === 0 ? (
            <p className="nx-hint">None declared.</p>
          ) : (
            <div className="nx-table-wrap">
              <table className="nx-table">
                <caption className="nx-visually-hidden">Declared connector versions</caption>
                <thead>
                  <tr>
                    <th scope="col">Version</th>
                    <th scope="col">Status</th>
                    <th scope="col">Approved digest</th>
                    <th scope="col">Declared</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.versions.map((version) => (
                    <tr key={version.connector_version_id}>
                      <td className="nx-table__primary">{version.version}</td>
                      <td>
                        <Pill tone={version.status === "active" ? "ok" : "quiet"}>
                          {version.status}
                        </Pill>
                      </td>
                      <td>
                        <Identifier value={version.approved_digest} />
                      </td>
                      <td>{when(version.declared_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="nx-hint" style={{ marginTop: 12 }}>
            The digest is what approval is *of*. A connector whose declared capabilities change no
            longer matches the digest somebody approved, and it is not enabled by having been
            enabled before.
          </p>
        </Card>
      )}
    </Show>
  );
}

export function ConnectedSystems() {
  const session = useSession();
  const [selected, setSelected] = useState<string | null>(null);

  /**
   * Both collections page, and the paging is the shared primitive's.
   *
   * The primitive reads pages as `PageOf`, which is narrower than the `Page`
   * the API returns: it does not carry `projection`. Nothing is discarded by
   * that here, and the proof is worth writing down because the next reader will
   * ask. `/core/connected-systems` answers from
   * `IntegrationRegistryService.connected_systems`, which returns
   * `Page.of(...)`; `/core/integrations` answers from `registrations`, which
   * returns `Page.of(...)` and is then rebuilt by the route into a
   * `Page[IntegrationSummary]` that passes only items, cursor, has_more and
   * limit. `Page.of` never sets `projection`, the field defaults to `None`, and
   * the only code in the platform that ever constructs a `PageProjection` is
   * the outbox service, which serves neither route. So `projection` is null on
   * every page this screen reads.
   */
  const readSystems = useCallback(
    (cursor: string | null, signal: AbortSignal) =>
      api.get<Page<ConnectedSystem>>("/core/connected-systems", {
        params: { limit: PAGE, cursor },
        signal,
      }),
    [],
  );
  const systems = usePagedCollection<ConnectedSystem>(readSystems);

  const readIntegrations = useCallback(
    (cursor: string | null, signal: AbortSignal) =>
      api.get<Page<IntegrationSummary>>("/core/integrations", {
        params: { limit: PAGE, cursor },
        signal,
      }),
    [],
  );
  // `GET /core/integrations` is guarded by `core.connected_system:read` — the
  // same authority as the catalogue above — so it is asked for on `system.read`.
  // It used to wait for `connector.read`, which the route never consults: a
  // caller holding the catalogue but not the connector registry was told their
  // registrations were withheld when the platform would have answered.
  const integrations = usePagedCollection<IntegrationSummary>(readIntegrations, {
    enabled: session.may("system.read"),
  });

  const registrationFor = (key: string): IntegrationSummary | undefined =>
    integrations.items.find((row) => row.source_system === key);

  /**
   * Why an absent registration is not yet the fact that there is none.
   *
   * This column is a join the screen performs itself: a system is shown as
   * registered because a registration naming it turned up in what was read.
   * The inverse does not follow. Registrations are a separate, paged,
   * separately-read collection, so "no registration found" can mean the
   * read was refused, that they have not arrived yet, or that
   * the one for this system is on a page nobody has asked for — and rendering
   * any of those as "not registered" states a lifecycle fact about somebody
   * else's integration that nothing established. `null` is the only case where
   * the absence is real: the whole collection was read, and this system was not
   * in it.
   */
  const whyRegistrationUnknown: string | null = !session.may("system.read")
    ? "Reading this tenant's registrations needs system.read, which is not granted to you."
    : integrations.state.kind === "forbidden"
      ? "Nexus refused to read this tenant's registrations, so whether this system is registered is not known."
      : integrations.state.kind !== "ready"
      ? "The registrations have not been read."
      : integrations.hasMore
        ? "Registrations are read a page at a time and matched to each system here, so the one this system has may be on a page not yet read."
        : null;

  return (
    <>
      <Banner title="A connected system is not a canonical Application.">
        This is integration metadata — something Nexus talks to. The applications the enterprise
        owns live in the enterprise graph, and the two are deliberately different objects.
      </Banner>

      <div className="nx-split">
        <Card
          title="Connected systems"
          actions={
            <button
              type="button"
              className="nx-button"
              onClick={() => {
                systems.refresh();
                integrations.refresh();
              }}
            >
              Refresh
            </button>
          }
          bodyless
        >
          <Show
            resource={systems.state}
            what="the connected systems"
            onRetry={systems.refresh}
            empty={() =>
              systems.items.length === 0 ? (
                <Empty title="No connected systems in this deployment." />
              ) : null
            }
          >
            {() => (
              <div className="nx-table-wrap">
                <table className="nx-table">
                  <caption className="nx-visually-hidden">Systems Nexus integrates with</caption>
                  <thead>
                    <tr>
                      <th scope="col">System</th>
                      <th scope="col">Kind</th>
                      <th scope="col">Registration</th>
                      <th scope="col">Auth</th>
                    </tr>
                  </thead>
                  <tbody>
                    {systems.items.map((system) => {
                      const registration = registrationFor(system.key);
                      return (
                        <tr key={system.key} aria-selected={selected === system.key}>
                          <td>
                            <button
                              type="button"
                              className="nx-rowbutton"
                              onClick={() => {
                                setSelected(system.key);
                              }}
                            >
                              <span className="nx-table__primary">{system.name}</span>
                              <span className="nx-table__secondary" style={{ display: "block" }}>
                                {system.key}
                              </span>
                            </button>
                          </td>
                          <td>{system.kind.replace(/_/g, " ")}</td>
                          <td>
                            {registration ? (
                              <Pill tone={REGISTRATION_TONE[registration.status] ?? "quiet"}>
                                {registration.status}
                              </Pill>
                            ) : whyRegistrationUnknown === null ? (
                              <span className="nx-hint">not registered</span>
                            ) : (
                              <span className="nx-hint" title={whyRegistrationUnknown}>
                                not yet known
                              </span>
                            )}
                          </td>
                          <td>{registration ? registration.auth_method.replace(/_/g, " ") : "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Show>
          <More
            collection={systems}
            label="Show more systems"
            hint="This is one page of the catalogue, not every system Nexus may integrate with."
            placement="bodyless-followed"
          />
          <More
            collection={integrations}
            label="Show more registrations"
            hint="Registrations are read a page at a time and matched to the systems above, so a system shown without one may be registered on a page not yet read."
            placement="bodyless"
          />
        </Card>

        {selected ? (
          <SystemDetail systemKey={selected} />
        ) : (
          <Card title="Select a system">
            <p className="nx-hint">
              Choose a system to see its connector contract, the capabilities it declares, and which
              version was approved.
            </p>
          </Card>
        )}
      </div>
    </>
  );
}
